import { ArrayHelpers, Field, FieldArray, FieldProps, Form, Formik, FormikErrors, FormikProps } from 'formik';
import { DeclineIcon, PlusCircleIcon } from '@synergy/theme';
import { FormHelperText, Grid, IconButton, Theme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Fragment, forwardRef, useEffect, useRef, useState } from 'react';
import { Locale } from '@synergy/models';
import { PoolButton, PoolModal, PoolTextField, PoolTypography, ScrollToFieldError } from '@synergy/components';
import { SubPageHandle, useFormikSubPage } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useParams } from 'react-router';

import * as ProductAttributeActions from './store/product-detail-attributes-actions';
import { Attribute } from '../../../../../common/models/attribute';
import { FormValues } from './models/form-values';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import { PoolAttributeAutocompleteList } from './components/pool-attribute-autocomplete-list';
import { ProductAttribute } from './models/product-attribute';
import { bestLocaleSelector } from '../../../../../common/selectors/locale-selectors';
import {
    productDetailAttributeFormDataSelector,
    productDetailAttributesBaselineSelector,
} from './store/product-detail-attributes-selectors';
import { productDetailAttributesValidationSchema } from './product-detail-attributes-validation-shema';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        padding: '24px 58px 48px 70px',
        paddingTop: 48,
    },
    gridContainer: {
        alignItems: 'center',

        '& > div:first-child, & > div:nth-child(2)': {
            paddingBottom: theme.spacing(0.75),
        },
    },
    label: {
        color: theme.general.colors.black,
        marginLeft: 24,
    },
    flexContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    button: {
        marginLeft: 14,
        width: 44,
        height: 44,
    },
}));

interface ModalData {
    index: number;
    attribute: {
        id: number | null;
        name: string;
    };
}

export interface ProductDetailAttributesProps {
    selectedLanguage: Locale;
}

export const ProductDetailAttributes = forwardRef<SubPageHandle, ProductDetailAttributesProps>((props, ref) => {
    const dispatch = useThunkDispatch();
    const classes = useStyles();
    const { id: productIdParam } = useParams<{ id: string }>();
    const intl = useIntl();

    const { formValues, errors, touched } = useAppSelector(productDetailAttributeFormDataSelector);

    // The attributes as the server has them — see PaloDirtyComparison.

    const baseline = useAppSelector(productDetailAttributesBaselineSelector);
    const productAttributes = useAppSelector(state => state.product.detail.attributes.attributes);
    const currentLocale = useAppSelector(bestLocaleSelector);
    const attributes = useAppSelector(state => state.attributes.sortedByName);

    const formikRef = useRef<FormikProps<FormValues>>(null);
    const [modalData, setModalData] = useState<ModalData | null>(null);

    useEffect(() => {
        if (!productAttributes && productIdParam) {
            dispatch(ProductAttributeActions.fetchAttributes(+productIdParam));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useFormikSubPage(
        ref,
        formikRef,
        (values: FormValues, isDirty: boolean, isValid: boolean, errs: FormikErrors<FormValues>) => {
            dispatch(ProductAttributeActions.updateAttributes(values, isDirty, isValid, errs));
        }
    );

    const availableAttributes = (values: FormValues, fieldValue?: number): Attribute[] => {
        const formAttributeIds = values.attributes.map(attribute => attribute.attributeId);
        return attributes
            .filter(attribute => fieldValue === attribute.id || !formAttributeIds.includes(attribute.id))
            .filter(attribute => attribute.locale === props.selectedLanguage);
    };

    const getAttributeValue = (id: number | null): Attribute => {
        const attribute = attributes.find(a => a.id === id);
        return {
            id: id ?? -1,
            name: attribute?.name ?? '',
            locale: attribute?.locale ?? currentLocale,
        };
    };

    const handleRemoveClick = (attribute: ProductAttribute, index: number) => {
        setModalData({
            index,
            attribute: {
                id: attribute.attributeId,
                name: attributes.find(a => a.id === attribute.attributeId)?.name ?? '',
            },
        });
    };

    const handleDelete = (arrayHelpers: ArrayHelpers) => {
        if (!modalData) {
            return;
        }
        setModalData(null);
        if (!formikRef.current) {
            return;
        }

        if (formikRef.current.values.attributes.length > 1) {
            arrayHelpers.remove(modalData.index);
        } else {
            formikRef.current.setFieldValue('attributes', [{ id: null, value: '' }]);
        }
    };

    return (
        <div className={classes.container}>
            <Formik
                onSubmit={() => Promise.resolve()}
                initialValues={formValues}
                enableReinitialize={true}
                initialErrors={errors}
                initialTouched={touched}
                innerRef={formikRef}
                validationSchema={productDetailAttributesValidationSchema}
            >
                {(formikProps: FormikProps<FormValues>) => (
                    <Form>
                        <PaloDirtyComparison baseline={baseline} />
                        <ScrollToFieldError />
                        <FieldArray name="attributes">
                            {(arrayHelpers: ArrayHelpers) => (
                                <Grid container={true} spacing={2} className={classes.gridContainer}>
                                    <Grid item={true} xs={8}>
                                        <PoolTypography
                                            typography="caption"
                                            className={classes.label}
                                            id="app.label.attribute"
                                        />
                                    </Grid>
                                    <Grid item={true} xs={4}>
                                        <PoolTypography
                                            typography="caption"
                                            className={classes.label}
                                            id="app.label.value"
                                        />
                                    </Grid>

                                    {formikProps.values.attributes.map((attribute, index) => {
                                        const attributeValue = getAttributeValue(attribute.attributeId);
                                        const readonly =
                                            !!attribute.attributeId && attributeValue.locale !== props.selectedLanguage;
                                        return (
                                            <Fragment key={index}>
                                                <Grid item={true} xs={8}>
                                                    <Field name={`attributes.${index}.attributeId`}>
                                                        {({ form, field, meta }: FieldProps<number>) => (
                                                            <>
                                                                <PoolAttributeAutocompleteList
                                                                    value={getAttributeValue(field.value)}
                                                                    items={availableAttributes(
                                                                        form.values,
                                                                        field.value
                                                                    )}
                                                                    locale={currentLocale}
                                                                    placeholder={intl.formatMessage({
                                                                        id: 'app.product.detail.attribute.select',
                                                                    })}
                                                                    onSelected={value =>
                                                                        form.setFieldValue(
                                                                            field.name,
                                                                            value?.id ?? null
                                                                        )
                                                                    }
                                                                    error={!!meta.touched && !!meta.error}
                                                                    readOnly={readonly}
                                                                    disablePreventOverflow={true}
                                                                />
                                                                {!!meta.touched && !!meta.error && (
                                                                    <FormHelperText error={true}>
                                                                        <FormattedMessage id={meta.error} />
                                                                    </FormHelperText>
                                                                )}
                                                            </>
                                                        )}
                                                    </Field>
                                                </Grid>
                                                <Grid item={true} xs={4} className={classes.flexContainer}>
                                                    <Field name={`attributes.${index}.value`}>
                                                        {({ field, meta }: FieldProps<string>) => (
                                                            <PoolTextField
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                placeholder={intl.formatMessage({
                                                                    id: 'app.label.value',
                                                                })}
                                                                error={meta.touched && !!meta.error}
                                                                helperText={
                                                                    meta.touched && meta.error ? (
                                                                        <FormattedMessage id={meta.error} />
                                                                    ) : null
                                                                }
                                                                readOnly={readonly}
                                                                fullWidth={true}
                                                            />
                                                        )}
                                                    </Field>
                                                    <IconButton
                                                        onClick={() => handleRemoveClick(attribute, index)}
                                                        className={classes.button}
                                                    >
                                                        <DeclineIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Fragment>
                                        );
                                    })}
                                    {attributes.length > formikProps.values.attributes.length && (
                                        <Grid item={true} xs={12}>
                                            <PoolButton
                                                startIcon={<PlusCircleIcon color="primary" />}
                                                onClick={() => arrayHelpers.push({ id: null, value: '' })}
                                            >
                                                <FormattedMessage id="app.product.detail.addAttribute" />
                                            </PoolButton>
                                        </Grid>
                                    )}

                                    <PoolModal
                                        actionChildren={[
                                            <PoolButton
                                                key="1"
                                                onClick={() => setModalData(null)}
                                                variant="text"
                                                styleType="firstLevel"
                                            >
                                                <FormattedMessage id="app.common.cancel" />
                                            </PoolButton>,
                                            <PoolButton
                                                key="2"
                                                onClick={() => handleDelete(arrayHelpers)}
                                                color="secondary"
                                                styleType="firstLevel"
                                            >
                                                <FormattedMessage id="app.common.delete" />
                                            </PoolButton>,
                                        ]}
                                        isVisible={!!modalData}
                                        title={<FormattedMessage id="app.product.detail.attribute.delete" />}
                                        handleClose={() => setModalData(null)}
                                        hasModalTypography={true}
                                    >
                                        <PoolTypography
                                            id="app.product.detail.attribute.deleteWarning"
                                            values={{ value: modalData?.attribute.name }}
                                        />
                                    </PoolModal>
                                </Grid>
                            )}
                        </FieldArray>
                    </Form>
                )}
            </Formik>
        </div>
    );
});
